/**
 * Hash-based routing: parsing the URL hash into a Route and serializing it back.
 * Kept separate from App.tsx so the URL round-trip is independently testable
 * without pulling in the full component/auth/env dependency graph.
 */

import type { ExtensionType } from "../types/index";

export type Route =
  | { page: "catalog"; filterClients?: string[]; filterCategories?: string[]; filterQuery?: string; filterType?: ExtensionType; filterOfficial?: boolean; filterIdentities?: string[] }
  | { page: "detail"; installKey: string }
  | { page: "contribute" }
  | { page: "edit"; installKey: string; from?: "myskills" }
  | { page: "admin" }
  | { page: "help"; section?: HelpSectionId; topic?: HelpTopicId }
  | { page: "myskills" }
  | { page: "mcp"; filterQuery?: string; sort?: McpSort }
  | { page: "mcpserver"; serverName: string };

/** MCP Servers catalog sort. "name" is the default and is left out of the URL. */
export type McpSort = "name" | "recent";

// Each id needs a matching <Section id> in HelpPage.
export const SKILLS_HELP_SECTION_IDS = ["chat-cowork", "claude-code", "usage-figures", "contributing"] as const;
export const MCP_HELP_SECTION_IDS = [
  "mcp-what",
  "mcp-desktop",
  "mcp-tool-permissions",
  "mcp-cowork",
  "mcp-claude-code",
  "mcp-other-clients",
  "mcp-first-use",
  "mcp-managing",
  "mcp-troubleshooting",
  "mcp-request",
] as const;
export const HELP_SECTION_IDS = [...SKILLS_HELP_SECTION_IDS, ...MCP_HELP_SECTION_IDS] as const;
export type HelpSectionId = typeof HELP_SECTION_IDS[number];
export type McpHelpSectionId = typeof MCP_HELP_SECTION_IDS[number];

// Help is split into topics; "skills" is the default and is left out of the URL.
export const HELP_TOPIC_IDS = ["skills", "mcp"] as const;
export type HelpTopicId = typeof HELP_TOPIC_IDS[number];

/** The topic a section lives under, so a section link opens the right topic. */
export function helpTopicForSection(section: HelpSectionId): HelpTopicId {
  return (MCP_HELP_SECTION_IDS as readonly string[]).includes(section) ? "mcp" : "skills";
}

function parseHelpSectionParam(raw: string | null): HelpSectionId | undefined {
  return raw && (HELP_SECTION_IDS as readonly string[]).includes(raw) ? (raw as HelpSectionId) : undefined;
}

const VALID_CLIENT_VALUES = new Set(["claude-code", "claude-cowork", "claude-chat"]);
const LEGACY_CLIENT_URL_MAP: Record<string, string> = {
  "claude-desktop": "claude-chat",
  "cowork":         "claude-cowork",
};

function parseClientParam(raw: string): string | null {
  const remapped = LEGACY_CLIENT_URL_MAP[raw] ?? raw;
  if (VALID_CLIENT_VALUES.has(remapped)) return remapped;
  console.warn(`[parseHash] Unrecognized client URL value "${raw}" discarded`);
  return null;
}

// decodeURIComponent throws URIError on a malformed escape ("100%", or "%" left over after
// URLSearchParams has already decoded once). parseHash runs during the app's first render,
// so a throw there lands on the ErrorBoundary, whose Reload reloads the same hash. Returns
// null instead, so each caller picks its own fallback.
function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

// Chip names from the URL: decoded per segment (see routeToHash), trimmed, and deduped
// case-insensitively so "Jane Doe,jane doe" can't render two identical sections. A segment
// that won't decode is dropped rather than shown garbled.
function parseIdentitiesParam(raw: string | null): string[] | undefined {
  if (raw == null) return undefined;
  const seen = new Map<string, string>();
  for (const segment of raw.split(",")) {
    const value = safeDecode(segment)?.trim();
    if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
  }
  return seen.size > 0 ? [...seen.values()] : undefined;
}

export function parseHash(hash: string): Route {
  const [pathPart, queryPart] = hash.replace(/^#\/?/, "").split("?");
  const path = pathPart ?? "";
  if (path === "contribute") return { page: "contribute" };
  if (path === "help") {
    const params = new URLSearchParams(queryPart ?? "");
    const section = parseHelpSectionParam(params.get("section"));
    // A section implies its topic; an explicit topic only matters without one.
    if (section) return { page: "help", section };
    return params.get("topic") === "mcp" ? { page: "help", topic: "mcp" } : { page: "help" };
  }
  if (path === "mcp") {
    const params = new URLSearchParams(queryPart ?? "");
    const route: Route = { page: "mcp" };
    const q = params.get("q");
    if (q) route.filterQuery = q;
    if (params.get("sort") === "recent") route.sort = "recent";
    return route;
  }
  if (path.startsWith("mcp/")) {
    const rawName = path.slice("mcp/".length);
    const serverName = safeDecode(rawName) ?? rawName;
    if (serverName) return { page: "mcpserver", serverName };
    return { page: "mcp" };
  }
  if (path === "admin") return { page: "admin" };
  if (path === "my-skills") return { page: "myskills" };
  if (path.startsWith("edit/")) {
    const rawKey = path.slice("edit/".length);
    const installKey = safeDecode(rawKey) ?? rawKey;
    if (installKey) {
      // Only the literal "myskills" is an accepted origin — anything else is
      // ignored so the edit page's back defaults to the marketplace.
      const from = new URLSearchParams(queryPart ?? "").get("from");
      return from === "myskills"
        ? { page: "edit", installKey, from: "myskills" }
        : { page: "edit", installKey };
    }
  }
  if (path.startsWith("skill/")) {
    const rawKey = path.slice("skill/".length);
    const installKey = safeDecode(rawKey) ?? rawKey;
    if (installKey) return { page: "detail", installKey };
  }
  const params = new URLSearchParams(queryPart ?? "");
  const typeParam = params.get("type");
  return {
    page: "catalog",
    filterClients: params.get("client")
      ?.split(",")
      .filter(Boolean)
      .map(parseClientParam)
      .filter((v): v is string => v !== null) ?? undefined,
    filterCategories: params.get("category")?.split(",").filter(Boolean) ?? undefined,
    filterQuery: params.get("q") ?? undefined,
    filterType: typeParam === "plugin" || typeParam === "skill" ? typeParam : undefined,
    filterOfficial: params.get("official") === "1" ? true : undefined,
    // Multi-select author/org chips. Each value is URL-encoded individually before joining on
    // "," since a real name can itself contain a comma, so it's decoded per segment, not on
    // the joined whole.
    filterIdentities: parseIdentitiesParam(params.get("identities")),
  };
}

export function routeToHash(route: Route): string {
  switch (route.page) {
    case "catalog": {
      const params = new URLSearchParams();
      if (route.filterClients?.length) params.set("client", route.filterClients.join(","));
      if (route.filterCategories?.length) params.set("category", route.filterCategories.join(","));
      if (route.filterQuery) params.set("q", route.filterQuery);
      if (route.filterType) params.set("type", route.filterType);
      if (route.filterOfficial) params.set("official", "1");
      if (route.filterIdentities?.length) {
        params.set("identities", route.filterIdentities.map((v) => encodeURIComponent(v)).join(","));
      }
      const qs = params.toString();
      return qs ? `#/?${qs}` : "#/";
    }
    case "contribute": return "#/contribute";
    case "help": {
      if (route.section) return `#/help?section=${route.section}`;
      return route.topic === "mcp" ? "#/help?topic=mcp" : "#/help";
    }
    case "mcp": {
      const params = new URLSearchParams();
      if (route.filterQuery) params.set("q", route.filterQuery);
      if (route.sort === "recent") params.set("sort", "recent");
      const qs = params.toString();
      return qs ? `#/mcp?${qs}` : "#/mcp";
    }
    case "mcpserver": return `#/mcp/${encodeURIComponent(route.serverName)}`;
    case "admin": return "#/admin";
    case "myskills": return "#/my-skills";
    case "detail": return `#/skill/${encodeURIComponent(route.installKey)}`;
    case "edit": {
      // `#/edit/{key}` must stay byte-identical when `from` is absent (existing
      // tests + external links depend on it); append the query only when set.
      const base = `#/edit/${encodeURIComponent(route.installKey)}`;
      return route.from === "myskills" ? `${base}?from=myskills` : base;
    }
    default: {
      // Exhaustiveness guard: a new Route variant must add a case above, or this
      // fails to compile (tsconfig has no noImplicitReturns to catch it otherwise).
      const _exhaustive: never = route;
      return _exhaustive;
    }
  }
}
